import { html } from 'lit-element';

import connect from '@elab/core/store/connect';
import createSelectors from '@elab/core/utils/dynaForms/selectors';
import { compareState } from '@elab/core/utils/webComponent';
import { selectors as authSelectors } from '@elab/core/store/auth';
import { getValueByStorePath } from '@elab/core/utils/helpers';

import { FormElement } from './Form';

/**
 * @prop {Boolean} [loading] - for adding css class 'button--loading' when component in loading state
 * */

const fieldType = {
    textArea: 'TEXT_AREA',
};

export class FormButton extends FormElement {
    static get properties() {
        return {
            metadata: { type: Array },
            step: { type: Number },
            classModifier: {
                type: String,
                attribute: 'class-modifier',
            },
            buttonLabel: {
                type: String,
                attribute: 'button-label',
            },
            defaultSize: {
                type: Boolean,
                attribute: 'default-size',
            },
            disabledAttr: { type: Boolean, attribute: 'disabled' },
            disabled: { type: Boolean, attribute: false },
            loading: { type: Boolean, attribute: false },
            authLoading: { type: Boolean, attribute: false },
            isFormFocused: { type: Boolean, attribute: false },
            isTextAreaFieldFocused: { type: Boolean, attribute: false },
            disabledOnError: {
                type: Boolean,
                attribute: 'disabled-on-error',
            },
            disabledRef: {
                type: Array,
                attribute: 'disabled-ref',
            },
            disabledIf: {
                type: String,
                attribute: 'disabled-if',
            },
            disabledIfFalse: {
                type: Boolean,
                attribute: 'disabled-if-false',
                value: false,
            },
            noScroll: { type: Boolean, attribute: 'no-scroll' },
            popupName: {
                type: String,
                attribute: 'popup-name',
            },
            popupUrl: {
                type: String,
                attribute: 'popup-url',
            },
            noArrow: {
                type: Boolean,
                attribute: 'no-arrow',
            },
            animated: { type: Boolean },
        };
    }

    constructor() {
        super();

        this.disabled = false;
        this.isFormFocused = false;
        this.isTextAreaFieldFocused = false;
        this.disabledOnError = false;
        this.loading = false;
        this.authLoading = false;
        this.disabledRef = [];
        this.metadata = [];
        this.popupName = '';
        this.popupUrl = '';
        this.buttonLabel = '';
        this.step = 0;
        this.noArrow = false;
    }

    shouldUpdate(changedProps) {
        return [
            ['defaultSize'],
            ['disabled'],
            ['step'],
            ['loading'],
            ['authLoading'],
            ['metadata', 'length'],
        ]
            .map(item => compareState(this, changedProps, ...item))
            .some(Boolean);
    }

    connectedCallback() {
        super.connectedCallback();
        window.document.addEventListener('keydown', this.handleKeyDown);
    }

    disconnectedCallback() {
        window.document.removeEventListener('keydown', this.handleKeyDown);
        super.disconnectedCallback();
    }

    handleKeyDown = evt => {
        if (evt.key === 'Enter' && this.isFormFocused && !this.isTextAreaFieldFocused) {
            this.submit(evt);
        }
    };

    submit = evt => {
        evt.target.blur();
        const dialogInfo = {
            name: this.popupName,
            iframeUrl: this.popupUrl,
        };
        super.submit(evt, dialogInfo, this.noScroll);
    };

    render() {
        const {
            classModifier = '',
            defaultSize = false,
            disabled,
            noArrow,
            animated,
            loading,
            authLoading,
        } = this;
        const setLoading = loading ? 'button--loading' : authLoading ? 'button--loading' : '';
        return html`
            <e2-button
                type="submit"
                .handleClick=${this.submit}
                size="${defaultSize ? '' : 'big'}"
                class-modifier="${classModifier}
                    ${setLoading}"
                ?disabled="${disabled}"
                ?no-arrow="${noArrow}"
                ?animated="${animated}"
                button-label="${this.buttonLabel}"
            >
            </e2-button>
        `;
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = createSelectors(ctx.storeName);
    const isLoading = selectors.isLoading(state);
    const disabledRef = Array.isArray(ctx.disabledRef) ? ctx.disabledRef : [ctx.disabledRef];

    // this filter will remove fields which are not present in the current form context
    const filteredDisabledRef = disabledRef.filter(fieldName =>
        selectors.getDataField(state, fieldName),
    );

    const hasEmptyFields = filteredDisabledRef.some(
        ref => !selectors.getDataFieldValue(state, ref),
    );
    const hasFieldsInError = filteredDisabledRef.some(ref =>
        selectors.getDataFieldError(state, ref),
    );

    const hasIfConditional = ctx.disabledIf
        ? ctx.disabledIfFalse
            ? !getValueByStorePath(ctx.disabledIf, state)
            : getValueByStorePath(ctx.disabledIf, state)
        : false;

    return {
        step: selectors.getStep(state) || 0,
        metadata: selectors.getMetadata(state) || [],
        disabled:
            hasIfConditional ||
            hasEmptyFields ||
            (ctx.disabledOnError && hasFieldsInError) ||
            !!ctx.disabledAttr,
        loading: isLoading,
        authLoading: authSelectors.isLoading(state),
        isFormFocused: selectors.isFocused(state),
        isTextAreaFieldFocused: selectors.getCurrentFocusedField(state).type === fieldType.textArea,
    };
};

export default connect(mapStateToProps)(FormButton);
